import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { mockFirms } from '../api/mockData';
import client from '../api/client';

interface Firm {
  id: string;
  name: string;
  industry?: string;
  website?: string;
  logo?: string;
}

export const MembersPage: React.FC = () => {
  const navigate = useNavigate();
  const [firms, setFirms] = useState<Firm[]>([]);
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchFirms = async () => {
      try {
        const response = await client.get('/firms');
        setFirms(response.data.firms);
      } catch (err) {
        console.warn('Using mock data (backend not available)');
        setFirms(mockFirms);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFirms();
  }, []);

  const filtered = firms
    .filter(f => f.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="page">
      {/* Hero Header */}
      <section
        className="section--sm"
        style={{
          background: 'linear-gradient(135deg, var(--c-primary) 0%, var(--c-primary-dark) 100%)',
          paddingTop: 60,
          paddingBottom: 60,
        }}
      >
        <div className="container">
          <span className="section-label" style={{color: 'rgba(255,255,255,.65)'}}>
            Our Community
          </span>
          <h1 style={{color: 'white', marginBottom: 12}}>Member Firms</h1>
          <p style={{color: 'rgba(255,255,255,.75)', maxWidth: 520, fontSize: 16}}>
            The RDF represents firms committed to regulatory best practice.
          </p>
        </div>
      </section>

      {/* Members Section */}
      <section className="section">
        <div className="container">
          <div className="members-search">
            <input
              type="text"
              className="form-input"
              placeholder={`Search ${firms.length} member firms…`}
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{maxWidth: 400}}
            />
          </div>

          {isLoading ? (
            <div className="loading">
              <div style={{fontSize: 12, color: 'var(--c-text-muted)'}}>Loading firms...</div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="empty-state">
              <h3>No firms match your search</h3>
              <p className="text-muted">Try a different term.</p>
            </div>
          ) : (
            <>
              <p style={{fontSize: 13, color: 'var(--c-text-muted)', marginBottom: 18}}>
                Showing {filtered.length} of {firms.length} members
              </p>
              <div className="members-grid">
                {filtered.map(f => (
                  <div key={f.id} className="member-card">
                    {f.logo ? (
                      <img
                        src={f.logo}
                        alt={f.name}
                        style={{
                          width: 64,
                          height: 64,
                          flexShrink: 0,
                          borderRadius: 12,
                          objectFit: 'cover',
                          border: '1px solid var(--c-border)',
                          background: '#f8f8f8',
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: 64,
                          height: 64,
                          flexShrink: 0,
                          borderRadius: 12,
                          background: 'var(--c-primary)',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontFamily: 'var(--f-h)',
                          fontSize: 15,
                          fontWeight: 700,
                        }}
                      >
                        {f.name.slice(0, 2).toUpperCase()}
                      </div>
                    )}
                    <span className="member-name">{f.name}</span>
                  </div>
                ))}
              </div>
            </>
          )}

          <div
            style={{
              marginTop: 48,
              padding: 28,
              background: 'var(--c-primary-10)',
              borderRadius: 'var(--r-lg)',
              border: '1px solid var(--c-border)',
            }}
          >
            <h3 style={{marginBottom: 8}}>Not seeing your firm?</h3>
            <p style={{color: 'var(--c-text-muted)', fontSize: 14, marginBottom: 16}}>
              If your firm is a retail broker, vendor, or consultant, and is not yet a member of the RDF, please get in touch to discuss membership.
            </p>
            <button className="btn btn-primary btn-sm" onClick={() => { navigate('/contact'); window.scrollTo(0, 0); }}>Contact the RDF</button>
          </div>
        </div>
      </section>
    </div>
  );
};
